// 本文中の Markdown 表の「保存済み列幅」を扱う共有モジュール。
//
// - 保存形（`TableColumnWidths` の配列。index は本文中の表のブロック順）の検証と、
//   列幅更新 op の適用をここへ集約する。サーバ（op-applier）とクライアントの楽観適用が
//   同じ関数を通ることで、確定結果と楽観結果が必ず一致する。
// - 列幅の「表示への当て方」（一致確認と比例縮小）は markdown-source-map.ts が持つ。
//   本モジュールは保存側だけを扱い、表の解析結果には依存しない。

import type { CanvasObject, TableColumnWidths, TableColumnWidthsList } from "./canvas-object";
import { TABLE_MIN_COLUMN_EM } from "./markdown-source-map";

/** 1 つの本文で列幅を保存できる表の最大数（ブロック順 index は 0〜この値 − 1）。 */
export const MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES = 32;

/** 列幅を保存できる表の最大列数。 */
export const MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_COLUMNS = 32;

/** 保存できる 1 列の最大幅（em）。 */
export const MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM = 512;

/** 保存できる 1 列の最小幅（em）。表示側の最小列幅と同じ値にそろえる。 */
export const MARKDOWN_TABLE_COLUMN_WIDTH_MIN_EM = TABLE_MIN_COLUMN_EM;

/** ヘッダ指紋の形式（8 桁の小文字 16 進数）。自動へ戻す更新では空文字列を使う。 */
export const MARKDOWN_TABLE_HEADER_KEY_PATTERN = /^[0-9a-f]{8}$/;

/** 列幅更新 op が運ぶ内容（`UpdateTableColumnWidthsOp` から type と共通メタを除いたもの）。 */
export interface TableColumnWidthsUpdateInput {
  /** 本文中の表のブロック順 index。 */
  readonly tableIndex: number;
  /** 本文中の表の総数。これを超える保存値は切り捨てる。 */
  readonly tableCount: number;
  /** 新しい列幅。空配列は「保存を破棄して自動列幅へ戻す」を表す。 */
  readonly widthsEm: readonly number[];
  /** 保存時点の列数（`widthsEm.length` と一致する。空配列のときは 0）。 */
  readonly columnCount: number;
  /** 保存時点のヘッダ指紋。空配列のときは空文字列。 */
  readonly headerKey: string;
}

/** 列幅更新の結果。失敗時は利用者向けの理由を返す。 */
export type TableColumnWidthsEditResult =
  | { readonly ok: true; readonly object: CanvasObject }
  | { readonly ok: false; readonly reason: string };

/** 列幅を保存できない対象（本文中に Markdown 表を持ち得ないオブジェクト）を拒否する理由。 */
export const TABLE_COLUMN_WIDTHS_TARGET_REASON =
  "表の列幅を保存できるのは、テキスト図形と付箋だけです。";

/** 範囲・形式を満たさない列幅更新を拒否する理由。 */
export const TABLE_COLUMN_WIDTHS_INVALID_REASON = "表の列幅の指定が不正です。";

/** 1 件ぶんの保存値が保存形として正しいかを判定する。 */
function isValidTableColumnWidthsEntry(value: unknown): value is TableColumnWidths {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<TableColumnWidths>;
  if (typeof entry.headerKey !== "string") return false;
  if (!MARKDOWN_TABLE_HEADER_KEY_PATTERN.test(entry.headerKey)) return false;
  if (!Array.isArray(entry.widthsEm)) return false;
  if (entry.widthsEm.length < 1) return false;
  if (entry.widthsEm.length > MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_COLUMNS) return false;
  if (entry.columnCount !== entry.widthsEm.length) return false;
  return entry.widthsEm.every((width) => isValidColumnWidthEm(width));
}

/** 1 列ぶんの幅（em）が保存できる範囲の有限数かを判定する。 */
function isValidColumnWidthEm(width: unknown): width is number {
  return (
    typeof width === "number" &&
    Number.isFinite(width) &&
    width >= MARKDOWN_TABLE_COLUMN_WIDTH_MIN_EM &&
    width <= MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM
  );
}

/**
 * 保存済み列幅の配列が保存形（表 32 件まで・列 32 件まで・幅は範囲内の有限数・
 * `columnCount` と `widthsEm.length` の一致・8 桁小文字 16 進のヘッダ指紋）を満たすか判定する。
 * 欠落は `null` だけで表す（`undefined` は保存時に要素ごと消えて添字がずれるため認めない）。
 */
export function isValidTableColumnWidthsList(value: unknown): value is TableColumnWidthsList {
  if (!Array.isArray(value)) return false;
  if (value.length > MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES) return false;
  return value.every((entry) => entry === null || isValidTableColumnWidthsEntry(entry));
}

/** 指定した表（ブロック順 index）の保存済み列幅。未保存・範囲外は `null`。 */
export function tableColumnWidthsAt(
  list: TableColumnWidthsList | undefined,
  tableIndex: number
): TableColumnWidths | null {
  return list?.[tableIndex] ?? null;
}

/** 末尾の連続する `null`（＝自動列幅）を取り除く。保存内容を必要な長さだけに保つ。 */
function trimTrailingAuto(list: readonly (TableColumnWidths | null)[]): TableColumnWidthsList {
  let end = list.length;
  while (end > 0 && list[end - 1] == null) end -= 1;
  return end === list.length ? list : list.slice(0, end);
}

/** 更新 op の内容が、スキーマと同じ範囲・形式を満たすかを適用側でも確かめる。 */
function isValidUpdateInput(input: TableColumnWidthsUpdateInput): boolean {
  const { columnCount, headerKey, tableCount, tableIndex, widthsEm } = input;
  if (!Number.isInteger(tableCount) || tableCount < 0) return false;
  if (tableCount > MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES) return false;
  if (!Number.isInteger(tableIndex) || tableIndex < 0) return false;
  if (tableIndex >= MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES) return false;
  // 本文中に存在しない表を指す更新は、保存しても表示に使われないまま配列だけを伸ばす。
  if (tableIndex >= tableCount) return false;
  if (!Array.isArray(widthsEm)) return false;
  if (columnCount !== widthsEm.length) return false;
  // 空配列は「自動列幅へ戻す」。このときだけ列数 0・ヘッダ指紋なしを許す。
  if (widthsEm.length === 0) return headerKey === "";
  return isValidTableColumnWidthsEntry({ columnCount, headerKey, widthsEm });
}

/**
 * 列幅更新 op を、テキスト図形・付箋へ適用する。サーバとクライアントの楽観適用が共有する。
 *
 * - 配列が `tableIndex` より短い場合は、`null`（自動列幅）で埋めてから差し替える。
 * - `widthsEm` が空配列のときは、その index へ `null` を入れる（自動列幅へ戻す）。
 * - `tableCount` を超える要素は切り捨てる（本文編集で表が減ったときの余剰値を掃除する）。
 *   そのうえで、末尾が連続する `null` になった場合はさらに切り詰める。
 * - 結果が空配列になる場合はフィールドごと落とし、未保存の状態と保存内容を一致させる。
 */
export function applyTableColumnWidthsUpdate(
  object: CanvasObject,
  input: TableColumnWidthsUpdateInput
): TableColumnWidthsEditResult {
  if (object.type !== "text" && object.type !== "sticky") {
    return { ok: false, reason: TABLE_COLUMN_WIDTHS_TARGET_REASON };
  }
  if (!isValidUpdateInput(input)) {
    return { ok: false, reason: TABLE_COLUMN_WIDTHS_INVALID_REASON };
  }
  const { columnCount, headerKey, tableCount, tableIndex, widthsEm } = input;
  const next: (TableColumnWidths | null)[] = (object.tableColumnWidths ?? []).slice(0, tableCount);
  while (next.length < tableIndex) next.push(null);
  const entry: TableColumnWidths | null =
    widthsEm.length === 0 ? null : { columnCount, headerKey, widthsEm: [...widthsEm] };
  next[tableIndex] = entry;
  const trimmed = trimTrailingAuto(next);
  return {
    ok: true,
    object: {
      ...object,
      tableColumnWidths: trimmed.length === 0 ? undefined : trimmed,
    } as CanvasObject,
  };
}
